import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  faCircleNotch,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { EmployeeModel } from '../../api/models';
import { ClientRepService } from '../../api/services';
import { ToastService } from '../../services/toast.service';

interface RepOption {
  label: string;
  value: number;
  rep: EmployeeModel;
}

export interface SelectedRep {
  rep: EmployeeModel;
  surveyCount: number;
}

interface StoredRep {
  employeeId: number;
  surveyCount: number;
}

const SELECTED_REPS_STORAGE_KEY = 'surveys.repSelection.selectedReps';
const DEFAULT_SURVEY_COUNT = 0;

@Component({
  selector: 'app-rep-selection',
  templateUrl: './rep-selection.component.html',
  styleUrl: './rep-selection.component.scss',
})
export class RepSelectionComponent implements OnInit {
  faCircleNotch = faCircleNotch;
  faPlus = faPlus;
  faTrash = faTrash;

  areRepsLoading: boolean = false;
  allRepOptions: RepOption[] = [];
  availableRepOptions: RepOption[] = [];
  selectedRepId: number | null = null;
  selectedReps: SelectedRep[] = [];

  @Output() selectedRepsChange = new EventEmitter<SelectedRep[]>();

  constructor(
    private clientRepService: ClientRepService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.getClientReps();
  }

  getClientReps() {
    if (this.areRepsLoading === true) {
      // Avoid concurrent duplicate http requests if already loading.
      return;
    }
    this.areRepsLoading = true;

    this.clientRepService.getClientReps$Json$Response().subscribe({
      next: (response) => {
        if (!response.body) {
          console.error(response.body);
          this.toastService.showToast('danger', 'Problem loading client reps');
        } else {
          this.allRepOptions = response.body
            .filter((r) => r.employeeId !== undefined && r.employeeId !== null)
            .map((r) => ({
              label: this.getRepName(r),
              value: r.employeeId!,
              rep: r,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

          this.restoreSelectedReps();
        }

        this.areRepsLoading = false;
      },
      error: (e) => {
        this.areRepsLoading = false;
        console.error('Error loading client reps:', e);
        this.toastService.showToast('danger', 'Problem loading client reps');
      },
    });
  }

  addRep() {
    if (this.selectedRepId === null) {
      return;
    }

    const option = this.allRepOptions.find(
      (o) => o.value === this.selectedRepId
    );
    if (!option) {
      return;
    }

    if (this.isRepSelected(option.value)) {
      return;
    }

    this.selectedReps = [
      ...this.selectedReps,
      { rep: option.rep, surveyCount: DEFAULT_SURVEY_COUNT },
    ];
    this.selectedRepId = null;
    this.onSelectedRepsChanged();
  }

  removeRep(selected: SelectedRep) {
    this.selectedReps = this.selectedReps.filter(
      (s) => s.rep.employeeId !== selected.rep.employeeId
    );
    this.onSelectedRepsChanged();
  }

  /**
   * The input is restricted to whole numbers, but it still emits null when the
   * field is cleared, so fall back to the default rather than storing null.
   */
  onSurveyCountChanged(selected: SelectedRep, value: number | null) {
    selected.surveyCount = this.coerceSurveyCount(value);
    this.saveSelectedReps();
    this.selectedRepsChange.emit(this.selectedReps);
  }

  getRepName(rep: EmployeeModel): string {
    if (rep.name) {
      return rep.name;
    }

    const fullName = `${rep.firstName ?? ''} ${rep.lastName ?? ''}`.trim();
    if (fullName.length > 0) {
      return fullName;
    }

    return rep.email ?? `Rep ${rep.employeeId}`;
  }

  private coerceSurveyCount(value: unknown): number {
    if (typeof value !== 'number' || isNaN(value)) {
      return DEFAULT_SURVEY_COUNT;
    }

    return Math.max(DEFAULT_SURVEY_COUNT, Math.trunc(value));
  }

  private isRepSelected(employeeId: number): boolean {
    return this.selectedReps.some((s) => s.rep.employeeId === employeeId);
  }

  private refreshAvailableReps() {
    this.availableRepOptions = this.allRepOptions.filter(
      (o) => !this.isRepSelected(o.value)
    );
  }

  private onSelectedRepsChanged() {
    this.sortSelectedReps();
    this.refreshAvailableReps();
    this.saveSelectedReps();
    this.selectedRepsChange.emit(this.selectedReps);
  }

  private sortSelectedReps() {
    this.selectedReps = [...this.selectedReps].sort((a, b) =>
      this.getRepName(a.rep).localeCompare(this.getRepName(b.rep))
    );
  }

  /**
   * Rehydrates the selection from session storage. Only the employee id and
   * survey count are stored, so the rep details always come from the current
   * API response and ids that no longer resolve to a rep are dropped.
   */
  private restoreSelectedReps() {
    const storedReps = this.readStoredReps();

    this.selectedReps = storedReps
      .map((stored) => {
        const option = this.allRepOptions.find(
          (o) => o.value === stored.employeeId
        );
        return option
          ? { rep: option.rep, surveyCount: stored.surveyCount }
          : undefined;
      })
      .filter((s): s is SelectedRep => s !== undefined);

    this.sortSelectedReps();
    this.refreshAvailableReps();

    if (this.selectedReps.length !== storedReps.length) {
      // Prune ids that no longer resolve so we don't keep re-checking them.
      this.saveSelectedReps();
    }

    this.selectedRepsChange.emit(this.selectedReps);
  }

  private readStoredReps(): StoredRep[] {
    try {
      const stored = sessionStorage.getItem(SELECTED_REPS_STORAGE_KEY);
      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((entry) => {
          if (
            entry === null ||
            typeof entry !== 'object' ||
            typeof entry.employeeId !== 'number'
          ) {
            return undefined;
          }

          return {
            employeeId: entry.employeeId,
            surveyCount: this.coerceSurveyCount(entry.surveyCount),
          };
        })
        .filter((entry): entry is StoredRep => entry !== undefined);
    } catch (e) {
      console.error('Error reading selected reps from session storage:', e);
      return [];
    }
  }

  private saveSelectedReps() {
    try {
      const storedReps: StoredRep[] = this.selectedReps.map((s) => ({
        employeeId: s.rep.employeeId!,
        surveyCount: s.surveyCount,
      }));
      sessionStorage.setItem(
        SELECTED_REPS_STORAGE_KEY,
        JSON.stringify(storedReps)
      );
    } catch (e) {
      console.error('Error saving selected reps to session storage:', e);
    }
  }
}
